import { EpiLink } from '@api/types';
import { useAppDispatch, useEditMode, useInternalRedirect, useLinkData } from '@hooks';
import { Box, BoxProps, Link as MdLink, styled } from '@mui/material';
import { setAllModals } from '@store/modal/modal.slice';
import { MouseEvent } from 'react';

export type SmartLinkProps = Pick<BoxProps, 'sx'> &
  EpiLink & {
    children?: React.ReactNode;
    icon?: React.ReactNode;
    className?: string;
    disableInEdit?: boolean;
    download?: boolean;
    disabled?: boolean;
  };

const StyledLink = styled(MdLink)<SmartLinkProps>`
  cursor: pointer;
  text-decoration: none;
  display: block;
`;

/**
 * Smart Link.
 * Handle Link Collection Items: Content Link, Internal and External link.
 * Use Router Link from React Router if it's internal Link within app.
 * Use standard 'a' link for external links and edit mode.
 */
export const SmartLink = (props: SmartLinkProps) => {
  const { inEditMode, inBlockEdit, inEpiShell } = useEditMode();
  const { disableInEdit, children } = props;

  //  Do not wrap in link when in block edit and disableInEdit flag is true
  if (disableInEdit && inBlockEdit) {
    return <>{children}</>;
  }
  const shouldDisplayEditModeLink = inEditMode || inBlockEdit || inEpiShell;
  return shouldDisplayEditModeLink ? (
    <SmartLinkEditMode {...props} />
  ) : (
    <SmartLinkDisplayMode {...props} />
  );
};

const SmartLinkEditMode = (link: SmartLinkProps) => {
  const { href = '', title, text, target, className, children, icon, sx, disabled } = link;
  return (
    <StyledLink
      href={href}
      title={title}
      target={target}
      className={className}
      sx={sx}
      aria-disabled={disabled}
      tabIndex={disabled ? -1 : 0}
      style={disabled ? { pointerEvents: 'none' } : undefined}
    >
      {icon}
      <Box component='span' className='link-text'>
        {children ?? text}
      </Box>
    </StyledLink>
  );
};

const SmartLinkDisplayMode = (link: SmartLinkProps) => {
  const {
    href = '',
    title,
    text,
    target,
    className,
    children,
    icon,
    sx,
    download,
    disabled,
  } = link;
  const { inEditMode } = useEditMode();
  const dispatch = useAppDispatch();
  const redirect = useInternalRedirect();
  const { isInternal, path, isActivePath } = useLinkData(link);
  const clickHandler = (e: MouseEvent<HTMLElement>) => {
    if (disabled) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    e.preventDefault();
    redirect(path);
    dispatch(setAllModals(false));
  };
  const mainClass = isActivePath ? className + ' active' : className;
  const shouldRenderAsRouterLink = isInternal && !inEditMode && !download;
  const content = (
    <>
      {icon}
      <Box component='span' className='link-text'>
        {children ?? text}
      </Box>
    </>
  );

  return shouldRenderAsRouterLink ? (
    <StyledLink
      href={path}
      onClick={clickHandler}
      title={title}
      className={mainClass}
      sx={sx}
      aria-disabled={disabled}
      tabIndex={disabled ? -1 : 0}
      style={disabled ? { pointerEvents: 'none' } : undefined}
    >
      {content}
    </StyledLink>
  ) : (
    <StyledLink
      href={href}
      title={title}
      target={target}
      className={mainClass}
      sx={sx}
      download={download}
      aria-disabled={disabled}
      tabIndex={disabled ? -1 : 0}
      style={disabled ? { pointerEvents: 'none' } : undefined}
    >
      {content}
    </StyledLink>
  );
};
